import { Inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { APP_TOP_LEVEL_PATH } from '@epicuro-next/navigation/constants';
import {
  BROWSER_STORAGE_SERVICE,
  BROWSER_STORAGE_TOKEN,
} from '@epicuro-next/platform/storage';
import {
  resolveActiveAccount,
  selectActiveAccountId,
} from '@epicuro-next/state/accounts-state';
import { ACCOUNT_ID, patchSession } from '@epicuro-next/state/session-state';
import { getPulseUIRedirectTo } from '@epicuro-next/utilities-browser';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Injectable()
export class ActiveAccountGuard {
  constructor(
    private store: Store,
    private router: Router,
    @Inject(BROWSER_STORAGE_TOKEN)
    private storageService: BROWSER_STORAGE_SERVICE,
  ) {}

  public canActivate(
    routeSnapshot: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return this.getAccountId(routeSnapshot).pipe(
      map((accountId) => {
        if (accountId && typeof accountId !== 'string') {
          accountId = accountId.toString();
        }

        this.store.dispatch(
          patchSession({ session: { [ACCOUNT_ID]: accountId } }),
        );

        if (accountId) {
          this.store.dispatch(resolveActiveAccount({ accountId }));
        }

        // as guard added to all routes need to handle guard works for itself account-selector route
        if (this.isSelectAccountUrl(state.url)) {
          // User try to go manually to the route after confirm selection
          if (accountId) {
            return this.router.createUrlTree(['/']);
          }

          // proceed navigation to select accounts
          return true;
        }

        if (accountId) {
          return true;
        }

        return this.router.createUrlTree([
          APP_TOP_LEVEL_PATH.accountSelector.path,
        ]);
      }),
    );
  }

  private isSelectAccountUrl(url: string): boolean {
    return url.includes(APP_TOP_LEVEL_PATH.accountSelector.path);
  }

  private getActiveAccountFromStore(): Observable<string | null> {
    return this.store.pipe(select(selectActiveAccountId), take(1));
  }

  private getAccountIdFromUrl(
    routeSnapshot: ActivatedRouteSnapshot,
  ): string | null {
    const redirectTo = getPulseUIRedirectTo();
    const parsedRedirectToUrl = redirectTo
      ? this.router.parseUrl(`/${redirectTo}`)
      : null;

    const urlResolverRedirectParam =
      (redirectTo?.includes(APP_TOP_LEVEL_PATH.urlResolver.path) &&
        parsedRedirectToUrl?.queryParams?.redirect) ??
      (routeSnapshot?.routeConfig?.path ===
        APP_TOP_LEVEL_PATH.urlResolver.path &&
        routeSnapshot?.queryParams?.redirect);

    if (urlResolverRedirectParam) {
      try {
        return JSON.parse(atob(urlResolverRedirectParam))?.accountId;
      } catch {
        return null;
      }
    }

    return null;
  }

  private getAccountId(routeSnapshot: ActivatedRouteSnapshot) {
    return combineLatest([
      of(this.getAccountIdFromUrl(routeSnapshot)),
      of(this.storageService.get(ACCOUNT_ID)),
      this.getActiveAccountFromStore(),
    ]).pipe(
      take(1),
      map(
        ([urlAccountId, storageAccountId, storeAccountId]) =>
          urlAccountId || storageAccountId || storeAccountId || null,
      ),
    );
  }
}
